/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/indent */
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { HIDDEN_COLUMN } from "../../../shared/components/data-table/data-table.component";
import { DataTableParams } from "../../../shared/components/data-table/data-table.type";
import { AnyObject, SelectProperty } from "../../../shared/types/app.type";
import {
  dotNotationToObject,
  mergeObjects,
  setValueToDeeperProperty,
  simpleObjectToDotNotationString,
} from "../../../shared/utils/objects/object";
import { compare } from "../../../shared/utils/values";
import { ReportingData, ReportingEntities, ReportingEntity, Rule, RuleSet } from "./reporting.type";

@Injectable({
  providedIn: "root",
})
export class ReportingService {
  basePath = `${environment.serverUrl}`;

  constructor(private http: HttpClient) {}

  getData(
    { entity, fields, query, config, exportAsCsv }: ReportingData,
    pagination?: Pick<DataTableParams, "pagination">,
  ): Observable<PaginatedResponse<ReportingEntities> | void> {
    /** @workaround - remove the unnecessary hidden column */
    fields = fields.filter((field) => field !== HIDDEN_COLUMN);
    const csvMapper: AnyObject<string> = this.parseFieldsToCsvMapper(fields, config);
    const select = this.parseFieldsToSelect(fields, config);
    const relations = this.parseFieldsToRelations(fields, config);
    const where = this.parseRulesToWhere(query, config);
    const _params = { select, relations, where, pagination };
    let params: HttpParams = new HttpParams();

    for (const key in _params) {
      if (_params[key]) {
        params = params.set(key, JSON.stringify(_params[key]));
      }
    }

    const path = {
      asset: "assets",
      contract: "contracts",
      ticket: "tickets",
    };

    let url = `${this.basePath}/${path[entity]}`;

    // If exportAsCsv is true it will export a csv file to the user email
    if (exportAsCsv) {
      url += "/queue-csv";
      params = params.set("csvMapper", JSON.stringify(csvMapper));
    }

    return this.http
      .get<PaginatedResponse<ReportingEntities> | void>(url, { params })
      .pipe(catchError(this.handleError<PaginatedResponse<ReportingEntities> | void>()));
  }

  /** Creates the "csv mapper" data used to send in the request */
  parseFieldsToCsvMapper(fields: string[], config: ReportingEntity<ReportingEntities>): AnyObject<string> {
    const parseRelationField = (relationField: SelectProperty<ReportingEntities>): string =>
      simpleObjectToDotNotationString(relationField);

    return fields.reduce((acc, fieldName: keyof ReportingEntities) => {
      const name = config.fields[fieldName].name;
      const csvMapperKey = config.fields[fieldName].csvMapperKey;
      const relationField = config.fields[fieldName].relationField;
      let key: string = fieldName;
      if (csvMapperKey) {
        key = csvMapperKey;
      } else if (relationField) {
        key = parseRelationField(relationField);
      }
      return { ...acc, [key]: name };
    }, {});
  }

  /** Creates the "select" data used to send in the request */
  private parseFieldsToSelect(
    fields: string[],
    config: ReportingEntity<ReportingEntities>,
  ): AnyObject<true | AnyObject<true>> {
    return fields.reduce((acc, fieldName: keyof ReportingEntities) => {
      const relationField = config.fields[fieldName].relationField;
      const extraSelectKeys = config.fields[fieldName].extraSelectKeys;
      return mergeObjects([acc, relationField || { [fieldName]: true }, extraSelectKeys]);
    }, {});
  }

  /** Creates the "relations" data used to send in the request */
  private parseFieldsToRelations(fields: string[], config: ReportingEntity<ReportingEntities>): AnyObject {
    const parseRelationField = (relationField: SelectProperty<ReportingEntities>): string =>
      simpleObjectToDotNotationString(relationField).split(".").slice(0, -1).join(".");

    const relationsAsDotNotation = Array.from(
      new Set(
        fields
          .filter((fieldName: keyof ReportingEntities) => config.fields[fieldName].relationField)
          .map((fieldName: keyof ReportingEntities) => parseRelationField(config.fields[fieldName].relationField)),
      ),
    ).sort((a, b) => compare(a, b));

    const relations = relationsAsDotNotation.map((key) => dotNotationToObject(key));

    return mergeObjects(relations);
  }

  /** Creates the "where" data used to send in the request */
  private parseRulesToWhere(
    query: RuleSet<ReportingEntities>,
    config: ReportingEntity<ReportingEntities>,
  ): AnyObject | AnyObject[] {
    // Here we get the "unsafeWhere" because until here we didn't parse/convert the relationship fields
    const unsafeWhere = this.parseRulesToUnsafeWhere(query.condition, query.rules, config);
    return this.parseToSafeWhere(unsafeWhere, config);
  }

  /** Parse the rules to a "unsafeWhere" */
  private parseRulesToUnsafeWhere(
    condition: string,
    rules: (Rule | RuleSet)[],
    config: ReportingEntity<ReportingEntities>,
  ): AnyObject | AnyObject[] {
    const isRuleSet = (obj: Rule | RuleSet): obj is RuleSet => (obj as RuleSet).condition !== undefined;

    const isAnd = condition === "and";

    const mapping = rules.reduce((acc, obj) => {
      // Map ruleset
      let mappedRuleSet = null;
      if (isRuleSet(obj)) {
        mappedRuleSet = this.parseRulesToUnsafeWhere(obj.condition, obj.rules, config);
      }

      // Map rule
      let mappedRule = null;
      if (!isRuleSet(obj)) {
        // Here we set the rule depending of the conditions
        const fieldType = config.fields[obj.field].type;
        if (fieldType === "date" && obj.operator === "$between") {
          mappedRule = { [obj.field]: { [obj.operator]: { from: obj.value, to: obj.auxValue } } };
        } else {
          mappedRule = { [obj.field]: { [obj.operator]: obj.value } };
        }
      }

      if (isAnd) {
        if (isRuleSet(obj)) {
          return { ...acc, ...mappedRuleSet };
        } else {
          return { ...acc, ...mappedRule };
        }
      } else {
        if (isRuleSet(obj)) {
          return [...acc, mappedRuleSet];
        } else {
          return [...acc, mappedRule];
        }
      }
    }, []);

    return mapping;
  }

  /** Parse a "unsafeWhere" to a "safeWhere" */
  private parseToSafeWhere(
    unsafeWhere: AnyObject | AnyObject[],
    config: ReportingEntity<ReportingEntities>,
  ): AnyObject | AnyObject[] {
    return this.handleParser(unsafeWhere, config);
  }

  /** Handle the parser */
  private handleParser(
    unsafeWhere: AnyObject | AnyObject[],
    config: ReportingEntity<ReportingEntities>,
  ): AnyObject | AnyObject[] {
    if (Array.isArray(unsafeWhere)) {
      unsafeWhere = this.parseArray(unsafeWhere, config);
    } else {
      unsafeWhere = this.parseObject(unsafeWhere, config);
    }
    return unsafeWhere;
  }

  /** Parse an array */
  private parseArray(unsafeWhere: AnyObject[], config: ReportingEntity<ReportingEntities>): AnyObject[] {
    const array = [];
    for (const obj of unsafeWhere) {
      array.push(this.handleParser(obj, config));
    }
    return array;
  }

  /** Parse an object */
  private parseObject(unsafeWhere: AnyObject, config: ReportingEntity<ReportingEntities>): AnyObject {
    const objectArray = [];
    const fields = config.fields;
    for (const key in unsafeWhere) {
      if (Object.prototype.hasOwnProperty.call(unsafeWhere, key)) {
        // Here we check if key is a valid field otherwise it returns an error
        if (!fields[key]) {
          throw new Error(
            "One or more filter combinations are not allowed. Please correct or remove these filters and try again",
          );
        }
        // Here we get the "relationField" for the current field.
        // If it exists then the field (object) will be replaced by the calculated object "obj",
        // otherwise, the original field (object) will be used.
        const relationField = fields[key].relationField;
        if (relationField) {
          // Using the calculated object
          const obj = setValueToDeeperProperty(unsafeWhere[key], relationField);
          objectArray.push(obj);
        } else {
          // Using the original object
          objectArray.push({ [key]: unsafeWhere[key] });
        }
      }
    }
    unsafeWhere = mergeObjects([...objectArray], { omitUndefinedProps: true });
    return unsafeWhere;
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: unknown): Observable<T> => {
      console.error(error);
      return throwError(error);
    };
  }
}
